import { Panel, Typography } from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useBenefit, useUpdateBenefit } from 'store/benefits';
import { useMe } from 'state/Administrators';
import { useRouteParams } from 'utils/useIdParam';
import { useModalContext } from 'store/modal';
import { DateTimeSelectModal } from 'components/modals';
import { usePredefinedToasts } from 'utils/Toast';
import { ROUTES } from 'config';
import { routerHistory } from 'utils/router-history';
import StatusDropdown from './StatusDropdown';

const BenefitInfoPanel = () => {
  const { t } = useTranslation();
  const { hasEditPermission } = useMe();
  const { id: benefitId } = useRouteParams(ROUTES.benefits.details);
  const { openModal } = useModalContext();
  const predefinedToasts = usePredefinedToasts();

  const { data: benefit } = useBenefit(benefitId);

  const { mutate: updateBenefit, isLoading: isUpdating } = useUpdateBenefit();

  if (!benefit) return null;

  const renderCounter = (labelKey: string, count: number) => (
    <Stack spacing="5px" width="90px">
      <Typography variant="p2" color="textSecondary" align="center">
        {t(`benefits.${labelKey}`)}
      </Typography>
      <Typography
        color="primary"
        align="center"
        style={{ fontSize: 26, fontWeight: 500 }}
      >
        {count}
      </Typography>
    </Stack>
  );

  return (
    <Panel sx={{ p: 2 }}>
      <StatusDropdown
        value={benefit.status === 'draft' ? 'draft' : benefit.publishStatus}
        disabled={isUpdating}
        menuOptions={[
          {
            key: 'publish',
            label: t('buttons.publish'),
            onClick: async () => {
              const response = await openModal({
                component: DateTimeSelectModal,
                props: {
                  title: t('benefits.setPublishDate'),
                  datePickerProps: {
                    maxDateTime: benefit.deactivatedAt
                      ? moment(benefit.deactivatedAt)
                      : undefined,
                  },
                },
              });

              if (response.action === 'CONFIRM') {
                if (
                  benefit.deactivatedAt &&
                  moment(response.date).isAfter(benefit.deactivatedAt)
                ) {
                  return toast.error(
                    t('benefits.publishDateAfterDeactivationDate'),
                  );
                }

                const { id, ...data } = benefit;
                updateBenefit(
                  {
                    benefitId,
                    data: {
                      ...data,
                      isDraft: false,
                      publishedAt: response.date,
                    },
                  },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      routerHistory.push(ROUTES.benefits.list);
                    },
                  },
                );
              }
            },
            visible: benefit.status === 'draft',
          },
          {
            key: 'unpublish',
            label: t('benefits.unpublish'),
            onClick: async () => {
              const activationDate = moment(benefit?.activatedAt);
              const publishDate = moment(benefit?.publishedAt);
              const startDate =
                activationDate.isValid() && publishDate.isValid()
                  ? moment.max(activationDate, publishDate)
                  : activationDate.isValid()
                  ? activationDate
                  : publishDate.isValid()
                  ? publishDate
                  : undefined;

              const response = await openModal({
                component: DateTimeSelectModal,
                props: {
                  title: t('benefits.unpublish'),
                  datePickerProps: {
                    minDateTime: startDate,
                  },
                  initialValues:
                    benefit.deactivatedAt &&
                    moment().isAfter(benefit.deactivatedAt)
                      ? { date: benefit.deactivatedAt }
                      : undefined,
                },
              });

              if (response.action === 'CONFIRM') {
                if (
                  activationDate.isValid() &&
                  moment(response.date).isBefore(activationDate)
                ) {
                  return toast.error(
                    t('benefits.deactivationDateBeforeActivationDate'),
                  );
                }

                if (
                  publishDate.isValid() &&
                  moment(response.date).isBefore(publishDate)
                ) {
                  return toast.error(
                    t('errors.deactivation_date_before_publish_date'),
                  );
                }

                const { id, ...data } = benefit;
                updateBenefit(
                  {
                    benefitId,
                    data: {
                      ...data,
                      isDraft: false,
                      publishedAt: response.date,
                    },
                  },
                  {
                    onSuccess: () => {
                      predefinedToasts.success.updated();
                      routerHistory.push(ROUTES.benefits.list);
                    },
                  },
                );
              }
            },
            visible: benefit.publishStatus === 'published',
          },
        ]}
        statuses={{
          draft: { label: t('status.draft'), color: 'textSecondary' },
          published: { label: t('status.published'), color: 'success' },
          unpublished: {
            label: t('status.unpublished'),
            color: 'textSecondary',
          },
        }}
        readOnly={!hasEditPermission(['benefits'])}
      />

      <Stack
        direction="row"
        justifyContent="space-between"
        mt="20px"
        px="10px"
        divider={
          <Divider
            orientation="vertical"
            flexItem
            sx={{ height: '40px', alignSelf: 'center' }}
          />
        }
      >
        {renderCounter('assignedEmployees', benefit.employeeCount)}
        {renderCounter('assignedGroups', benefit.groupCount)}
      </Stack>
    </Panel>
  );
};

export default BenefitInfoPanel;
